import React from 'react';

import { isServiceTitanPortalV2Company } from '_lib/api/apiTypes.shim';
import { Box } from '_lib/components/Box/Box';
import { ExternalLinkOrDefault } from '_lib/components/ExternalLinkOrDefault/ExternalLinkOrDefault';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyLabels } from '_lib/constants/company.constants';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';

import { AvailabilityScheduleMessage } from '../AvailabilityScheduleMessage/AvailabilityScheduleMessage';
import { CompanyContext, ZoneZipContext } from '../Company.slice';

export type CompanyScheduleUrlProps = {
  companyContext: Pick<CompanyContext, 'company'>;
  zoneZipContext?: ZoneZipContext;
  showAnimation?: boolean;
};

export const CompanyScheduleUrlDisplay = ({
  company,
  overrideZipMatch,
  selectedZipOrPostal,
  selectedZone
}: {
  company: Pick<CompanyContext['company'], 'isZoned' | 'callCenter' | 'dataProvider'>;
  overrideZipMatch?: ZoneZipContext['overrideZipMatch'];
  selectedZone?: ZoneZipContext['selectedZone'];
  selectedZipOrPostal?: ZoneZipContext['selectedZipOrPostal'];
}) => {
  if (company.isZoned && !selectedZone) return <Typography color='error' text={'Select zone to see info'} />;

  if (!company.isZoned && !selectedZipOrPostal && overrideZipMatch === null)
    return <Typography text='No zip code selected' />;

  if (!company.isZoned && selectedZipOrPostal && overrideZipMatch === false)
    return <Typography text={`Zip code out of service area (${selectedZipOrPostal})`} />;

  const scheduleUrl = company.isZoned ? selectedZone?.callCenter.scheduleUrl : company.callCenter.scheduleUrl;

  // If company is Service Titan Portal V2 and scheduleUrl is NOT set, then show specific ST message
  if (!scheduleUrl && isServiceTitanPortalV2Company(company))
    return (
      <Typography>
        <AvailabilityScheduleMessage company={company} />
      </Typography>
    );

  return <ExternalLinkOrDefault url={scheduleUrl || null} label={CompanyLabels.SCHEDULE_URL} />;
};

export const CompanyScheduleUrlHeaderBox = (props: CompanyScheduleUrlProps) => {
  const { companyContext, zoneZipContext, showAnimation } = props;

  return (
    <Box
      sx={(theme) =>
        showAnimation
          ? {
              marginLeft: -1,
              marginTop: -1,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '8px',
              zIndex: 0,
              '&::before': {
                content: '""',
                zIndex: -2,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(0deg)',
                width: '999px',
                height: '999px',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: '0 0',
                backgroundImage: `conic-gradient(
            rgba(52, 168, 82, 0) 0deg,
            rgba(52, 168, 82, 1) 3deg,
            rgba(255, 211, 20, 1) 8deg,
            rgba(255, 70, 65, 1) 13deg,
            rgba(49, 134, 255, 1) 18deg,
            rgba(49, 134, 255, 1) 22deg,
            rgba(49, 134, 255, 0) 28deg
          )`,
                animation: 'googleAiRotate 3s linear infinite, googleAiFadeOut 10s ease-out forwards',
                filter: 'blur(8px)'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                zIndex: -1,
                left: '3px',
                top: '3px',
                width: 'calc(100% - 6px)',
                height: 'calc(100% - 6px)',
                backgroundColor: theme.palette.background.default,
                borderRadius: '6px'
              },
              '@keyframes googleAiRotate': {
                to: {
                  transform: 'translate(-50%, -50%) rotate(360deg)'
                }
              },
              '@keyframes googleAiFadeOut': {
                '0%': {
                  opacity: 1
                },
                '80%': {
                  opacity: 1
                },
                '100%': {
                  opacity: 0
                }
              }
            }
          : { marginLeft: -1, marginTop: -1 }
      }>
      <Box sx={{ padding: 1 }}>
        <HeaderBox
          sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
          headerText='Schedule URL'
          infoIconText={companyContext.company.isZoned ? 'Info is specific to the selected zone' : undefined}>
          <CompanyScheduleUrlDisplay
            company={companyContext.company}
            overrideZipMatch={zoneZipContext?.overrideZipMatch}
            selectedZipOrPostal={zoneZipContext?.selectedZipOrPostal}
            selectedZone={zoneZipContext?.selectedZone}
          />
        </HeaderBox>
      </Box>
    </Box>
  );
};
